import { Button } from '@app/components/Button/Button';
import { Tabs } from '@app/components';
import { ConfirmDialog } from '@app/components/ConfirmDialog/ConfirmDialog';
import { Menu, MenuButton, MenuItem, MenuItems, Switch } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  BusinessMemberInterface,
  BusinessMemberPermissions,
  PERMISSION_LABELS,
  PERMISSION_CATEGORIES,
} from '@app/models/business';
import { BaseDialog } from '@app/components';
import PencilIcon from '@app/assets/icons/pencil.svg?react';
import ArrowLeftIcon from '@app/assets/icons/arrow-left.svg?react';
import LinkBrokenIcon from '@app/assets/icons/link-broken.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import InfoIcon from '@app/assets/icons/info.svg?react';
import NoPhoto from '@app/assets/images/no-photo.svg?react';
import { useMemberProfile } from './MemberProfile.hooks';
import { usePermissions } from '@app/contexts/PermissionContext';
import { useAppSelector } from '@app/redux/hooks';

const MEMBER_PROFILE_TABS = [
  { label: 'Profile information', value: 'profile' },
  { label: 'Activity', value: 'activity' },
];

const StatusBadge = ({ status }: { status: 'active' | 'pending' }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center text-sm text-base-raisinBlack">
        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-sm text-base-raisinBlack">
      <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
      Request sent
    </span>
  );
};

const MemberAvatar = ({ member }: { member: BusinessMemberInterface }) => {
  if (member.user?.photo) {
    const src = typeof member.user.photo === 'string' ? member.user.photo : (member.user.photo as any)?.url;
    return (
      <div className="flex-shrink-0 h-16 w-16 rounded-full overflow-hidden">
        <img
          src={src}
          alt={`${member.user.firstName} ${member.user.lastName}`}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  return <NoPhoto />;
};

const PermissionIcon = ({ granted }: { granted: boolean }) => {
  if (granted) {
    return (
      <span className="flex-shrink-0 w-7 h-7 rounded-sm bg-green-50 flex items-center justify-center">
        <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }
  return (
    <span className="flex-shrink-0 w-7 h-7 rounded-sm bg-red-50 flex items-center justify-center">
      <svg className="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </span>
  );
};

export const MemberProfile = () => {
  const {
    member,
    isLoading,
    error,
    activeTab,
    setActiveTab,
    showInfoModal,
    editingInfoJobTitle,
    setEditingInfoJobTitle,
    handleOpenInfoModal,
    handleSaveInfo,
    handleCloseInfoModal,
    showPermissionsModal,
    editingPermissions,
    setEditingPermissions,
    confirmDialog,
    setConfirmDialog,
    updateMemberMutation,
    removeMemberMutation,
    handleOpenPermissionsModal,
    handleSavePermissions,
    handleClosePermissionsModal,
    handleDisconnectMember,
    handleGoBack,
  } = useMemberProfile();
  const { can } = usePermissions();
  const currentUserId = useAppSelector((state) => state.auth.user?.id);
  const isSelf = member?.userId === currentUserId;

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex justify-center items-center h-64"
      >
        <div className="text-base-coolGray">Loading member...</div>
      </motion.div>
    );
  }

  if (error || !member) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col justify-center items-center h-64 gap-4"
      >
        <div className="text-red-500">Member not found.</div>
        <Button kind="secondary" onClick={handleGoBack}>
          Back to Team Members
        </Button>
      </motion.div>
    );
  }

  const memberName = member.user?.firstName && member.user?.lastName
    ? `${member.user.firstName} ${member.user.lastName}`
    : member.user?.email || member.email || 'Pending invitation';

  const permissions = (member.isOwner ? null : member.permissions) as BusinessMemberPermissions | null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="bg-white border-b border-b-base-ghostWhite">
        <div className="container mx-auto pt-4 px-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className="p-2 rounded-lg hover:bg-base-ghostWhite"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <p className="text-xs text-base-coolGray">Settings &gt; {memberName}</p>
                <div className="flex items-center gap-3">
                  <h2 className="font-bold text-[28px]">{memberName}</h2>
                  <StatusBadge status={member.userId === null ? 'pending' : 'active'} />
                  {member.isOwner && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Owner
                    </span>
                  )}
                </div>
              </div>
            </div>
            {!member.isOwner && !isSelf && can('editTeamMembers') && (
              <Menu>
                <MenuButton className="text-base-coolGray hover:text-base-raisinBlack p-2 rounded-lg hover:bg-base-ghostWhite">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </MenuButton>
                <MenuItems
                  transition
                  anchor="bottom end"
                  className="w-52 p-1 bg-white shadow-lg rounded-lg border border-base-ghostWhite transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 z-10"
                >
                  <MenuItem>
                    <button
                      onClick={handleDisconnectMember}
                      disabled={removeMemberMutation.isPending}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                    >
                      <LinkBrokenIcon className="w-5 h-5" />
                      Disconnect member
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            )}
          </div>
          <Tabs
            tabs={MEMBER_PROFILE_TABS}
            onClick={(value) => setActiveTab(value)}
            defaultValue={activeTab}
          />
        </div>
      </div>

      {/* Content */}
      {activeTab === 'activity' ? (
        <div className="container mx-auto p-4">
          <div className="bg-white p-8 flex flex-col items-center justify-center text-center">
            <p className="text-base-coolGray text-sm">Activity log coming soon.</p>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4 container mx-auto p-4">
        {/* Left column */}
        <div className="space-y-4">
          {/* Main Information card */}
          <div className="bg-white p-4 h-fit">
            <div className="flex items-center justify-between">
              <h5 className="font-bold text-sm">Main Information</h5>
              {!isSelf && !member.isOwner && can('editTeamMembers') && (
                <button
                  onClick={handleOpenInfoModal}
                  className="bg-base-antiFlash p-2 rounded-lg"
                >
                  <PencilIcon />
                </button>
              )}
            </div>
            <hr className="my-4" />
            <div className="space-y-4">
              <MemberAvatar member={member} />
              <table>
                <tbody>
                  <tr>
                    <td className="w-[130px]">
                      <span className="text-xs text-base-coolGray">FIRST NAME</span>
                    </td>
                    <td>
                      <span className="text-sm font-medium">
                        {member.user?.firstName || '—'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-[130px]">
                      <span className="text-xs text-base-coolGray">LAST NAME</span>
                    </td>
                    <td>
                      <span className="text-sm font-medium">
                        {member.user?.lastName || '—'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-[130px]">
                      <span className="text-xs text-base-coolGray">JOB TITLE</span>
                    </td>
                    <td>
                      <span className="text-sm font-medium">
                        {member.jobTitle || '—'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Member Contacts card */}
          <div className="bg-white p-4 h-fit">
            <h5 className="font-bold text-sm">Member Contacts</h5>
            <hr className="my-4" />
            <div className="space-y-3">
              <div>
                <span className="text-xs text-base-coolGray block mb-1">EMAIL</span>
                <span className="text-sm font-medium">
                  {member.user?.email || member.email || '—'}
                </span>
              </div>
              <div>
                <span className="text-xs text-base-coolGray block mb-1">PHONE NUMBER</span>
                <span className="text-sm font-medium">
                  {(member.user as any)?.phoneNumber || '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right column — Permissions card */}
        <div className="bg-white p-4 h-fit">
          <div className="flex items-center justify-between">
            <h5 className="font-bold text-sm">Permissions</h5>
            {!member.isOwner && !isSelf && can('editTeamMembers') && (
              <button
                onClick={handleOpenPermissionsModal}
                className="bg-base-antiFlash p-2 rounded-lg"
              >
                <PencilIcon />
              </button>
            )}
          </div>
          <hr className="my-4" />
          {member.isOwner ? (
            <p className="text-sm text-base-coolGray">
              As the business owner, this member has all permissions.
            </p>
          ) : (
            <div className="divide-y divide-base-ghostWhite">
              {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => (
                <div key={categoryKey} className="py-4 first:pt-0">
                  <h4 className="text-xs font-semibold text-base-coolGray mb-3 uppercase tracking-wider">
                    {category.label}
                  </h4>
                  <div className="space-y-3">
                    {category.permissions.map((permKey) => (
                      <div key={permKey} className="flex items-center gap-3">
                        <PermissionIcon granted={permissions?.[permKey] === true} />
                        <span className="text-sm text-base-raisinBlack">
                          {PERMISSION_LABELS[permKey]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Edit Member Information Modal */}
      <BaseDialog open={showInfoModal} onClose={handleCloseInfoModal} size="max-w-3xl" className="!max-w-[744px] !rounded-none">
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <PencilIcon width={24} height={24} />
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg">Edit member information</h5>
            <p className="text-base-coolGray text-sm">
              Fill in the information below to edit member information
            </p>
          </div>
          <div>
            <button type="button" onClick={handleCloseInfoModal}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-6 space-y-6">
          <div className="flex items-center gap-4">
            <MemberAvatar member={member} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-raisinBlack mb-2">
                First name
              </label>
              <input
                type="text"
                value={member.user?.firstName || ''}
                disabled
                className="w-full px-4 py-2 border border-base-ghostWhite rounded-lg text-sm bg-base-ghostWhite text-base-coolGray"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-raisinBlack mb-2">
                Last name
              </label>
              <input
                type="text"
                value={member.user?.lastName || ''}
                disabled
                className="w-full px-4 py-2 border border-base-ghostWhite rounded-lg text-sm bg-base-ghostWhite text-base-coolGray"
              />
            </div>
          </div>

          <div className="w-1/2 pr-2">
            <label className="block text-sm font-medium text-base-raisinBlack mb-2">
              Job title
            </label>
            <input
              type="text"
              value={editingInfoJobTitle}
              onChange={(e) => setEditingInfoJobTitle(e.target.value)}
              placeholder="e.g., Admin"
              className="w-full px-4 py-2 border border-base-ghostWhite rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-baseBlue"
            />
          </div>

          <div className="flex items-center gap-4 justify-center pt-2">
            <Button
              kind="secondary"
              className="w-full"
              onClick={handleCloseInfoModal}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              onClick={handleSaveInfo}
              disabled={updateMemberMutation.isPending}
              loading={updateMemberMutation.isPending}
            >
              Save changes
            </Button>
          </div>
        </div>
      </BaseDialog>

      {/* Permissions Edit Modal */}
      <BaseDialog open={showPermissionsModal} onClose={handleClosePermissionsModal} size="max-w-3xl" className="!max-w-[744px] !rounded-none">
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <svg className="w-6 h-6 text-base-raisinBlack" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg">Edit member permissions</h5>
            <p className="text-base-coolGray text-sm">
              Select the areas of the platform this member can edit
            </p>
          </div>
          <div>
            <button type="button" onClick={handleClosePermissionsModal}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => (
              <div key={categoryKey}>
                <h4 className="text-xs font-semibold text-base-coolGray uppercase tracking-wider">
                  {category.label}
                </h4>
                <hr className="my-3 border-base-ghostWhite" />
                <div className="space-y-3">
                  {category.permissions.map((permKey) => (
                    <div key={permKey}>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Switch
                          checked={editingPermissions[permKey] === true}
                          onChange={() =>
                            setEditingPermissions((prev) => ({
                              ...prev,
                              [permKey]: !prev[permKey],
                            }))
                          }
                          className="group relative flex h-6 w-12 cursor-pointer rounded-full bg-gray-200 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-brand-baseBlue flex-shrink-0"
                        >
                          <span
                            aria-hidden="true"
                            className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
                          />
                        </Switch>
                        <span className="text-sm text-base-raisinBlack">
                          {PERMISSION_LABELS[permKey]}
                        </span>
                      </label>
                      {permKey === 'respondToReviews' && (
                        <div className="flex items-start gap-2 mt-2">
                          <InfoIcon className="w-4 h-4 text-base-coolGray flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-base-coolGray">
                            Only members who are connected to your business review platforms as members can respond to reviews.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-4 justify-center mt-12">
            <Button kind="secondary" className="w-full" onClick={handleClosePermissionsModal}>
              Cancel
            </Button>
            <Button
              className="w-full"
              onClick={handleSavePermissions}
              disabled={updateMemberMutation.isPending}
              loading={updateMemberMutation.isPending}
            >
              Save changes
            </Button>
          </div>
        </div>
      </BaseDialog>

      {/* Disconnect Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        isLoading={removeMemberMutation.isPending}
      />
    </motion.div>
  );
};
